import { useHistory } from "react-router-dom";
import { Button } from "@/components/ui/button-tw";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { getMerchantStepPath, MERCHANT_LANDING_PATH } from "../constants";

const LoadingDots = () => (
  <span className="inline-flex items-end gap-0.5 ml-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="inline-block h-1 w-1 rounded-full bg-white animate-bounce"
        style={{ animationDelay: `${i * 0.15}s`, animationDuration: "0.8s" }}
      />
    ))}
  </span>
);

const LoadingOverlay = () => (
  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gray-50/80 backdrop-blur-[2px] rounded-2xl">
    <div className="relative flex items-center justify-center">
      <svg
        className="h-12 w-12 animate-spin"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="24"
          cy="24"
          r="20"
          stroke="currentColor"
          strokeWidth="4"
          className="text-gray-200"
        />
        <path
          d="M44 24c0-11.046-8.954-20-20-20"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          className="text-skin-primary"
        />
      </svg>
    </div>
    <p className="mt-4 text-sm font-medium text-gray-500">Processing…</p>
  </div>
);

interface StepLayoutProps {
  step: number;
  title?: string;
  children: React.ReactNode;
  onNext?: () => void;
  onBack?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  isLastStep?: boolean;
  hideBack?: boolean;
  backPath?: string;
  footerMessage?: React.ReactNode;
  isLoading?: boolean;
  wide?: boolean;
  fullHeight?: boolean;
}

export const StepLayout = ({
  step,
  title,
  children,
  onNext,
  onBack,
  nextLabel,
  nextDisabled,
  isLastStep,
  hideBack,
  backPath,
  footerMessage,
  isLoading,
  wide,
  fullHeight,
}: StepLayoutProps) => {
  const history = useHistory();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backPath) {
      history.push(backPath);
    } else if (step <= 1) {
      history.push(MERCHANT_LANDING_PATH);
    } else {
      history.push(getMerchantStepPath(step - 1));
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (!isLastStep) {
      history.push(getMerchantStepPath(step + 1));
    }
  };

  return (
    <div className={`px-6 py-8 md:px-12 md:py-10 ${fullHeight ? "flex flex-col flex-1 overflow-hidden" : "flex-1 overflow-y-auto"}`}>
      <div className={`${wide ? "max-w-6xl" : "max-w-3xl"} mx-auto ${fullHeight ? "flex flex-col flex-1 min-h-0" : ""}`}>
        <div className={`relative bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden ${fullHeight ? "flex flex-col flex-1 min-h-0" : ""}`}>
          {isLoading && <LoadingOverlay />}

          <div className={`p-8 ${fullHeight ? "flex-1 flex flex-col min-h-0 overflow-hidden" : ""}`}>
            {title && (
              <h2 className="text-lg font-bold text-gray-900 mb-5">{title}</h2>
            )}
            <div className={fullHeight ? "flex flex-col flex-1 min-h-0 gap-5" : "space-y-5"}>{children}</div>
          </div>

          {footerMessage && (
            <div className="px-8 pb-4 -mt-1">{footerMessage}</div>
          )}

          <div className="border-t border-gray-100 px-8 py-5 flex items-center justify-between shrink-0">
            {hideBack ? (
              <span />
            ) : (
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <Button
              variant="default"
              onClick={handleNext}
              disabled={nextDisabled}
            >
              {isLoading ? (
                <>
                  {nextLabel ?? (isLastStep ? "Finish" : "Next")}
                  <LoadingDots />
                </>
              ) : (
                <>
                  {nextLabel ?? (isLastStep ? "Finish" : "Next")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
